import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { of, switchMap } from 'rxjs';
import { APP_CONFIG, DeviceEnum, PAGE, PlanStepEnum, PortalPageEnum, SearchItems, ToastService } from '../../core';
import { PlanSendModalComponent, PlanUpgradeModalComponent } from '../../plan';
import { ListService, PlanService, TlhexService, UserService } from '../../services';
import { BaseComponent, ConfirmationComponent } from '../../shared';


/**
* Plan actions component on plan table.
*/
@Component({
	selector: 'plan-action',
	templateUrl: './plan-action.component.html',
	imports: [
		NgbDropdownModule
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PlanActionComponent extends BaseComponent {

	private readonly router = inject(Router);
	private readonly config = inject(APP_CONFIG);
	private readonly modalSrv = inject(NgbModal);
	private readonly planSrv = inject(PlanService);
	private readonly listSrv = inject(ListService);
	private readonly userSrv = inject(UserService);
	private readonly tlhexSrv = inject(TlhexService);
	private readonly toastSrv = inject(ToastService);


	isTlhexUser = input<boolean>(false);
	plan = input<SearchItems | null>(null);
	isReadonly = toSignal(
		toObservable(this.plan).pipe(
			switchMap(plan => plan ? this.userSrv.isCustomerCareOtherUser(plan.userId) : of(true))
		)
	);


	isPlanVersionValid = computed(() => !!this.plan()?.caseVersion && this.plan()!.caseVersion >= this.config.appVersion);

	/**
	* Check if view action is visible.
	*/
	isViewVisible = computed(() => !!this.plan() && this.plan()!.device !== DeviceEnum.ToBeDefined);

	/**
	* Check if myHexPlan case settings action is visible.
	*/
	isMyHexPlanSettingsVisible = computed(() => !!this.plan() && this.isTlhexUser() && (!!this.plan()!.caseIsPublished || !!this.plan()!.caseIsRevoked) && this.isPlanVersionValid());

	/**
	* Navigate to view case page.
	*/
	view(): void {
		let site;
		switch (this.plan()!.device) {
			case DeviceEnum.Fitbone: site = this.config.fitboneViewSite; break;
			case DeviceEnum.Tlhex: site = this.config.tlhexViewSite; break;
			default: site = this.config.nextrayViewSite; break;
		}
		globalThis.location.href = `${site}?caseGuid=${this.plan()!.planId}`;
	}

	/**
	* Navigate to myHexPlan case settings.
	*/
	viewPlanSettings(): void {
		if (!this.isReadonly()) {
			this.tlhexSrv.navigateToMyhexplan(PortalPageEnum.CaseSettings, undefined, this.plan()!.planId);
		}
	}

	/**
	* Navigate to plan edit page.
	*/
	edit(): void {
		if (!this.isReadonly()) {
			this.router.navigate([`/${PAGE.PLAN_EDIT}`], { queryParams: { planGuid: this.plan()!.planId } });
		}
	}

	/**
	* Open confirmation modal before delete plan.
	*/
	openDeleteModal(): void {
		if (!this.isReadonly() && !this.modalSrv.hasOpenModals()) {
			const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().PLAN_ACTION_COMPONENT_DELETE_MODAL_TITLE,
				message: this.labels().PLAN_ACTION_COMPONENT_DELETE_MODAL_MESSAGE,
				cancelBtn: this.labels().PLAN_ACTION_COMPONENT_DELETE_MODAL_NO,
				confirmBtn: this.labels().PLAN_ACTION_COMPONENT_DELETE_MODAL_YES
			};
			modalRef.result.then(() => this.delete()).catch(error => false);
		}
	}

	private delete(): void {
		this.planSrv.deletePlan(this.plan()!.planId!).subscribe(() => this.listSrv.search());
	}

	/**
	 * Check if send action is visible.
	 */
	isSendVisible = computed(() => this.isPlanVersionValid());

	/**
	* Open modal to send plan.
	*/
	openSendModal(): void {
		const modalRef = this.modalSrv.open(PlanSendModalComponent, {
			centered: true, backdrop: 'static', windowClass: 'modal-form', size: 'lg'
		});
		(modalRef.componentInstance as PlanSendModalComponent).planGuid = this.plan()!.planId!;
		(modalRef.componentInstance as PlanSendModalComponent).userGuid = this.plan()!.userId!;
	}

	/**
	* Check if edit is visible.
	*/
	isEditVisible = computed(() => !!this.plan() && !this.plan()!.caseIsPublished && !this.plan()!.caseIsRevoked && this.isPlanVersionValid());

	/**
	* Check if delete is visible.
	*/
	isDeleteVisible = computed(() => !!this.plan() && !this.plan()!.caseIsPublished && !this.plan()!.caseIsRevoked);

	/**
	 * Check if postop create action is visible.
	 */
	isPostopCreateVisible = computed(() => !!this.plan() && !this.plan()!.isPostOperative && [PlanStepEnum.Report, PlanStepEnum.Adjust].includes(this.plan()!.step!) && this.isPlanVersionValid());

	/**
	 * Open confirmation modal before postop creation.
	 */
	openPostopCreateModal(): void {
		if (!this.isReadonly() && !this.modalSrv.hasOpenModals()) {
			const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().PLAN_ACTION_COMPONENT_POSTOP_MODAL_TITLE,
				message: this.labels().PLAN_ACTION_COMPONENT_POSTOP_MODAL_MESSAGE,
				cancelBtn: this.labels().PLAN_ACTION_COMPONENT_POSTOP_MODAL_NO,
				confirmBtn: this.labels().PLAN_ACTION_COMPONENT_POSTOP_MODAL_YES
			};
			modalRef.result.then(() => this.createPostopPlan()).catch(error => false);
		}
	}

	private createPostopPlan(): void {
		this.tlhexSrv.createPostopPlan(this.plan()!.planId!, this.plan()!.userId).pipe(
			switchMap(planId => this.router.navigate([`/${PAGE.PLAN_EDIT}`], { queryParams: { planGuid: planId, checkupFirstAccess: true } }))
		).subscribe({ error: () => this.toastSrv.showError(this.labels().PLAN_ACTION_COMPONENT_CREATE_POSTOP_ERROR) });
	}

	/**
	* Check if analysis is visible.
	*/
	isAnalysisVisible = computed(() => !!this.plan() && !this.plan()!.caseIsRevoked && this.isPlanVersionValid());

	/**
	* Navigate to ray site.
	*/
	navigateToApp(): void {
		let appRedirectUrl;
		if (this.plan()!.device === DeviceEnum.Tlhex) {
			appRedirectUrl = this.config.tlhexSite;
		} else if (this.plan()!.step === PlanStepEnum.RPM) {
			appRedirectUrl = this.config.fitboneSite;
		} else {
			appRedirectUrl = this.config.nextraySite;
		}
		globalThis.location.href = `${appRedirectUrl}?caseGuid=${this.plan()!.planId}`;
	}

	/**
	 * Check if upgrade action is visible.
	 */
	isUpgradeVisible = computed(() => !this.isPlanVersionValid() && !this.plan()?.caseIsRevoked);

	/**
	 * Open confirmation modal before plan upgrade.
	 */
	openUpgradeModal(): void {
		if (!this.isReadonly() && !this.modalSrv.hasOpenModals()) {
			const modalRef = this.modalSrv.open(PlanUpgradeModalComponent, {
				centered: true, backdrop: 'static', windowClass: 'modal-form', size: 'lg'
			});
			(modalRef.componentInstance as PlanUpgradeModalComponent).planGuid = this.plan()!.planId!;
			(modalRef.componentInstance as PlanUpgradeModalComponent).device = this.plan()!.device;
			(modalRef.componentInstance as PlanUpgradeModalComponent).item.set(this.plan());
		}
	}
}
